import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Stack, 
  Paper, 
  Box 
} from '@mui/material';
import { ConfirmDialog } from '@personal-dashboard/ui';
import HistoryIcon from '@mui/icons-material/History';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface Lesson {
  id: string;
  title: string;
  createdAt: string;
}

interface LessonHistoryListProps {
  pastLessons: Lesson[];
  selectedPastLessonId: string | null;
  onSelectLesson: (id: string) => void;
  hasActiveLesson: boolean;
}

const LessonHistoryList: React.FC<LessonHistoryListProps> = ({
  pastLessons,
  selectedPastLessonId,
  onSelectLesson,
  hasActiveLesson
}) => {
  const [pendingLessonId, setPendingLessonId] = useState<string | null>(null);

  const handleItemClick = (lessonId: string) => {
    if (hasActiveLesson) {
      setPendingLessonId(lessonId);
    } else {
      onSelectLesson(lessonId);
    }
  };

  const handleConfirm = () => {
    if (pendingLessonId) {
      onSelectLesson(pendingLessonId);
      setPendingLessonId(null);
    }
  };

  return (
    <>
      <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <HistoryIcon /> Previous Lessons
          </Typography>
          {pastLessons.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No completed lessons yet. Complete a session to see it logged here!
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ maxHeight: 350, overflowY: 'auto', pr: 0.5 }}>
              {pastLessons.map((lesson) => {
                const isSelected = selectedPastLessonId === lesson.id;
                return (
                  <Paper
                    key={lesson.id}
                    elevation={0}
                    onClick={() => handleItemClick(lesson.id)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      backgroundColor: isSelected ? 'action.hover' : 'background.paper',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }} noWrap>
                          {lesson.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(parseInt(lesson.createdAt)).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!pendingLessonId}
        title="Open Past Lesson"
        message="You have an active chat session. This won't close your active session — view the past lesson anyway?"
        confirmLabel="View Lesson"
        severity="info"
        onConfirm={handleConfirm}
        onCancel={() => setPendingLessonId(null)}
      />
    </>
  );
};

export default LessonHistoryList;
